import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface TeacherClass {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  student_count: number;
  assignment_count: number;
}

interface ClassStudent {
  id: number;
  username: string;
  email: string;
  enrolled_at: string;
}

interface AvailableStudent {
  id: number;
  username: string;
  email: string;
}

const TeacherClasses: React.FC = () => {
  const [classes, setClasses] = useState<TeacherClass[]>([]);
  const [selectedClass, setSelectedClass] = useState<TeacherClass | null>(null);
  const [classStudents, setClassStudents] = useState<ClassStudent[]>([]);
  const [availableStudents, setAvailableStudents] = useState<AvailableStudent[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddStudents, setShowAddStudents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [className, setClassName] = useState('');
  const [classDescription, setClassDescription] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTeacherClasses();
      if (data.success) {
        setClasses(data.classes);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const loadClassStudents = async (classId: number) => {
    try {
      const data = await apiService.getClassStudents(classId);
      if (data.success) {
        setClassStudents(data.students);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load class students');
    }
  };

  const loadAvailableStudents = async () => {
    try {
      const data = await apiService.getAvailableStudents();
      if (data.success) {
        // Filter out students already in the class
        const studentIdsInClass = new Set(classStudents.map(s => s.id));
        const available = data.users.filter((u: any) => !studentIdsInClass.has(u.id));
        setAvailableStudents(available);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load available students');
    }
  };

  const handleCreateClass = async () => {
    if (!className.trim()) {
      setError('Class name is required');
      return;
    }

    try {
      const data = await apiService.createClass(className, classDescription);
      if (data.success) {
        setShowCreateForm(false);
        setClassName('');
        setClassDescription('');
        await loadClasses();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create class');
    }
  };

  const handleUpdateClass = async () => {
    if (!selectedClass || !className.trim()) {
      setError('Class name is required');
      return;
    }

    try {
      const data = await apiService.updateClass(selectedClass.id, className, classDescription);
      if (data.success) {
        setShowEditForm(false);
        setSelectedClass(null);
        setClassName('');
        setClassDescription('');
        await loadClasses();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update class');
    }
  };

  const handleDeleteClass = async (classId: number) => {
    if (!window.confirm('Delete this class? This will also delete all assignments in this class.')) {
      return;
    }

    try {
      const data = await apiService.deleteClass(classId);
      if (data.success) {
        if (selectedClass?.id === classId) {
          setSelectedClass(null);
        }
        await loadClasses();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete class');
    }
  };

  const handleViewClass = async (cls: TeacherClass) => {
    setSelectedClass(cls);
    await loadClassStudents(cls.id);
  };

  const handleAddStudentsToClass = async () => {
    if (!selectedClass || selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      const data = await apiService.addStudentsToClass(selectedClass.id, selectedStudentIds);
      if (data.success) {
        setShowAddStudents(false);
        setSelectedStudentIds([]);
        await loadClassStudents(selectedClass.id);
        await loadClasses(); // Refresh counts
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add students to class');
    }
  };

  const handleRemoveStudent = async (studentId: number) => {
    if (!selectedClass) return;
    if (!window.confirm('Remove this student from the class?')) return;

    try {
      const data = await apiService.removeStudentFromClass(selectedClass.id, studentId);
      if (data.success) {
        await loadClassStudents(selectedClass.id);
        await loadClasses(); // Refresh counts
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove student');
    }
  };

  const startEdit = (cls: TeacherClass) => {
    setSelectedClass(cls);
    setClassName(cls.name);
    setClassDescription(cls.description || '');
    setShowEditForm(true);
  };

  const startAddStudents = async () => {
    setShowAddStudents(true);
    await loadAvailableStudents();
  };

  if (loading) {
    return <div className="teacher-classes">Loading classes...</div>;
  }

  return (
    <div className="teacher-classes">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Class List View */}
      {!selectedClass && !showCreateForm && !showEditForm && (
        <div className="classes-list-view">
          <div className="classes-header">
            <h3>📚 My Classes</h3>
            <button onClick={() => setShowCreateForm(true)} className="create-class-btn">
              ➕ Create New Class
            </button>
          </div>

          {classes.length === 0 ? (
            <div className="empty-state">
              <p>No classes yet. Create your first class to get started!</p>
            </div>
          ) : (
            <div className="classes-grid">
              {classes.map(cls => (
                <div key={cls.id} className="class-card">
                  <div className="class-card-header">
                    <h4>{cls.name}</h4>
                    <div className="class-actions">
                      <button onClick={() => startEdit(cls)} title="Edit" className="edit-btn">
                        ✏️
                      </button>
                      <button onClick={() => handleDeleteClass(cls.id)} title="Delete" className="delete-btn">
                        🗑️
                      </button>
                    </div>
                  </div>
                  {cls.description && <p className="class-description">{cls.description}</p>}
                  <div className="class-stats">
                    <span>👥 {cls.student_count} students</span>
                    <span>📝 {cls.assignment_count} assignments</span>
                  </div>
                  <button onClick={() => handleViewClass(cls)} className="view-class-btn">
                    View Class →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Class Form */}
      {showCreateForm && (
        <div className="class-form">
          <h3>Create New Class</h3>
          <div className="form-group">
            <label>Class Name *</label>
            <input
              type="text"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              placeholder="e.g., Spanish 101, Biology Advanced"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={classDescription}
              onChange={(e) => setClassDescription(e.target.value)}
              placeholder="Optional description of this class..."
              rows={3}
            />
          </div>
          <div className="form-actions">
            <button onClick={handleCreateClass} className="submit-btn">Create Class</button>
            <button onClick={() => {
              setShowCreateForm(false);
              setClassName('');
              setClassDescription('');
            }} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Class Form */}
      {showEditForm && selectedClass && (
        <div className="class-form">
          <h3>Edit Class</h3>
          <div className="form-group">
            <label>Class Name *</label>
            <input
              type="text"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              placeholder="e.g., Spanish 101, Biology Advanced"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={classDescription}
              onChange={(e) => setClassDescription(e.target.value)}
              placeholder="Optional description of this class..."
              rows={3}
            />
          </div>
          <div className="form-actions">
            <button onClick={handleUpdateClass} className="submit-btn">Update Class</button>
            <button onClick={() => {
              setShowEditForm(false);
              setSelectedClass(null);
              setClassName('');
              setClassDescription('');
            }} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Class Detail View */}
      {selectedClass && !showEditForm && !showAddStudents && (
        <div className="class-detail-view">
          <div className="class-detail-header">
            <button onClick={() => setSelectedClass(null)} className="back-btn">
              ← Back to Classes
            </button>
            <h3>{selectedClass.name}</h3>
            <button onClick={startAddStudents} className="add-students-btn">
              ➕ Add Students
            </button>
          </div>

          {selectedClass.description && (
            <p className="class-detail-description">{selectedClass.description}</p>
          )}

          <div className="class-students-section">
            <h4>Students ({classStudents.length})</h4>
            {classStudents.length === 0 ? (
              <div className="empty-state">
                <p>No students in this class yet. Add students to get started!</p>
              </div>
            ) : (
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Enrolled</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classStudents.map(student => (
                    <tr key={student.id}>
                      <td>{student.username}</td>
                      <td>{student.email}</td>
                      <td>{new Date(student.enrolled_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => handleRemoveStudent(student.id)}
                          className="remove-student-btn"
                          title="Remove from class"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Add Students to Class */}
      {showAddStudents && selectedClass && (
        <div className="add-students-form">
          <div className="add-students-header">
            <button onClick={() => {
              setShowAddStudents(false);
              setSelectedStudentIds([]);
            }} className="back-btn">
              ← Back
            </button>
            <h3>Add Students to {selectedClass.name}</h3>
          </div>

          <div className="students-selection">
            {availableStudents.length === 0 ? (
              <div className="empty-state">
                <p>No available students to add. All students are already in this class!</p>
              </div>
            ) : (
              <div className="students-checklist">
                {availableStudents.map(student => (
                  <label key={student.id} className="student-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStudentIds([...selectedStudentIds, student.id]);
                        } else {
                          setSelectedStudentIds(selectedStudentIds.filter(id => id !== student.id));
                        }
                      }}
                    />
                    <span className="student-info">
                      <strong>{student.username}</strong>
                      <small>{student.email}</small>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              onClick={handleAddStudentsToClass}
              disabled={selectedStudentIds.length === 0}
              className="submit-btn"
            >
              Add {selectedStudentIds.length} Student{selectedStudentIds.length !== 1 ? 's' : ''}
            </button>
            <button onClick={() => {
              setShowAddStudents(false);
              setSelectedStudentIds([]);
            }} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherClasses;
